import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { MotifModule } from "@ey-xd/motif-wc-angular"; 
import {
  IconoirCustomFlagSolid,
  IconoirWhiteFlag,
  IconoirCustomThumbsUpSolid,
  IconoirCustomThumbsDownSolid,
  IconoirInfoCircle,
} from '@ey-xd/motif-icon';
import { AgentFiveService } from 'app/core/service/agent-five/agent-five.service';
import { take } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from 'app/core/service/toast/toast.service';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'app-column',
  standalone: true,
  imports: [CommonModule, MotifModule],
  templateUrl: './column.component.html',
  styleUrl: './column.component.scss',
})
export class ColumnComponent implements OnChanges {
  @Input() row: any = {};
  @Input() field!: string;
  @Input() type: string | undefined = 'text'; // Default type

  //icons
  IconoirCustomFlagSolid = IconoirCustomFlagSolid;
  IconoirWhiteFlag = IconoirWhiteFlag;
  IconoirCustomThumbsUpSolid = IconoirCustomThumbsUpSolid;
  IconoirCustomThumbsDownSolid = IconoirCustomThumbsDownSolid;
  IconoirInfoCircle = IconoirInfoCircle;

  // To put in the column the info from the report used files
  reportFilesDetails: {
    pages: string[];
    filename: string;
    fileId: string;
  }[] = [];

  // Zoom related properties for breakpoint observer
  isZoomed: boolean = false;

  // tooltip management
  @ViewChildren('tooltipIcon') tooltipIcons!: QueryList<
    ElementRef<HTMLElement>
  >;

  constructor(
    private agentFiveService: AgentFiveService,
    private toastService: ToastService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.breakpointObserver
      .observe(['(max-width: 800px)'])
      .subscribe((result) => {
        this.isZoomed = result.matches;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['row']) {
      this.reportFilesDetails = this.mapPages(changes['row'].currentValue);
    }
  }

  mapPages(row: any): { pages: string[]; filename: string; fileId: string }[] {
    let toret: any[] = [];

    if (row['page'] && row['filenames']) {
      toret = row['filenames'].map((name: string, index: number) => {
        return {
          pages: row['page'][index].map((page: number) => `P.${page}`),
          filename: this.getFileName(name),
          fileId: row['fileIds'][index],
        };
      });
      return toret;
    }
    return toret;
  }

  goToPage(page: string, fileId: string) {
    const p: number = Number(page.split('.')[1]);

    this.agentFiveService
      .getPDFPage(fileId)
      .pipe(take(1))
      .subscribe({
        next: (data: Blob) => {
          const blobUrl = URL.createObjectURL(data);
          window.open(`${blobUrl}#page=${p}`, '_blank');
          // Cleanup after a short delay to prevent premature revocation
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 5000);
        },
        error: (error: HttpErrorResponse) => {
          const message: string =
            error.error?.message ||
            'An unexpected error occurred. If the error persist, please contact servicedesk@ey.com';
          this.toastService.showToast('error', `${message}`);
        },
      });
  }

  onTooltipClick(
    event: MouseEvent,
    isNativeElementClick: boolean = true
  ): void {
    const iconTrigger = event.currentTarget as HTMLElement;

    // for when is .click() so it does not fall in a loop
    if (isNativeElementClick) {
      return;
    }

    // If clicking the same icon again, close it and clear currentTooltip
    if (
      this.agentFiveService.currentTooltip &&
      this.agentFiveService.currentTooltip.id === iconTrigger.id
    ) {
      this.agentFiveService.currentTooltip = null;
      return;
    }

    // If another tooltip was open, close it
    if (
      this.agentFiveService.currentTooltip &&
      this.agentFiveService.currentTooltip.id !== iconTrigger.id
    ) {
      this.agentFiveService.currentTooltip.click();
      this.agentFiveService.currentTooltip = null;
    }

    // Store new trigger as active
    this.agentFiveService.currentTooltip = iconTrigger;
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.agentFiveService.currentTooltip) {
      this.agentFiveService.currentTooltip.click();
      this.agentFiveService.currentTooltip = null;
      return;
    }
    return;
  }

  // Returns file name without the extension of the file
  private getFileName(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');

    // If there's no dot or it's the first character, return the file name as-is
    if (lastDotIndex === -1 || lastDotIndex === 0) {
      return fileName;
    }

    // Return the file name without the extension
    return fileName.substring(0, lastDotIndex);
  }

  formatCurrency(value: string): string {
    if (value === null || value.toUpperCase() === 'N/A') {
      return `N/A`;
    }
    return value;
  }

  formatPercentage(value: string): string {
    if (
      value === null ||
      value.trim() === '' ||
      value.toLowerCase() === 'nan'
    ) {
      return '';
    }
    if (value.split('.')[1] === '0') {
      // 100.0
      return value.split('.')[0] + '%';
    }
    if (value.includes('(') || value.includes(')')) {
      if (value.split('.')[1] === '0)') {
        // (100.0)
        return value.split('.')[0] + ')' + '%';
      }
    }
    return value + '%';
  }

  // to format bullet points
  formatTooltipContent(value: string): string {
    if (!value) {
      return value;
    }

    const bullets = ['•'];
    let formattedValue = value.replace(/\n/g, ' \n');

    bullets.forEach((bullet: string) => {
      const regex = new RegExp(`\\s*${bullet}`, 'g');
      formattedValue = formattedValue.replace(regex, `\n${bullet}`);
    });

    return formattedValue.trim();
  }
}
